const baseUrl = import.meta.env.VITE_CONNECT_BFF_BASE_URL;

if (!baseUrl) throw new Error("VITE_CONNECT_BFF_BASE_URL is required.");

export interface Team {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    ownerUserId: string;
    visibility: string;
}

export interface Channel {
    id: string;
    teamId: string;
    name: string;
    description: string | null;
    kind: string;
    isDefault: boolean;
}

export interface ChannelMember {
    id: string;
    userId: string;
    userEmail: string;
    displayName: string | null;
    role: string;
    joinedAt: string;
    /** Last time this member opened the channel — drives read-receipt avatars on the latest message they've seen. */
    lastReadAt?: string | null;
}

export interface ChatMessage {
    id: string;
    containerId: string;
    parentMessageId: string | null;
    authorUserId: string;
    authorEmail: string;
    authorDisplayName: string | null;
    bodyHtml: string;
    isEdited: boolean;
    isPinned: boolean;
    /** "Standard" | "Important" | "Urgent" — older payloads may omit this field. */
    importance?: MessageImportance;
    sentAt: string;
    editedAt: string | null;
    /** Channel posts: optional subject line (absent when none). */
    subject?: string | null;
    /** Channel posts: announcement style — the subject is a headline on an accent banner. */
    isAnnouncement?: boolean;
}

export type MessageImportance = "Standard" | "Important" | "Urgent";

export interface DirectChat {
    id: string;
    participantA: string;
    participantB: string;
}

/** What POST /api/chats/open opened: `people` are the others in it. */
export interface OpenChatResult {
    kind: "dm" | "group";
    id: string;
    title: string;
    people: { userId: string; email: string; displayName: string | null }[];
}

async function fetchJson<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers });
    if (!res.ok) throw new Error(`Connect BFF ${path} → ${res.status}`);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

/** A failed Connect BFF call: `message` is the server's ProblemDetails title when it sent one. */
export class ConnectApiError extends Error {
    readonly status: number;
    /** The problem's `type` URI when the server sent one (e.g. …/app-not-licensed). */
    readonly problemType?: string;
    /** People the server could not find (…/people-not-in-directory). */
    readonly unresolved?: string[];
    constructor(message: string, status: number, problemType?: string, unresolved?: string[]) {
        super(message);
        this.name = "ConnectApiError";
        this.status = status;
        this.problemType = problemType;
        this.unresolved = unresolved;
    }
}

/** Like fetchJson, but throws a ConnectApiError carrying the status and the server's error title. */
async function fetchProblem<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers });
    if (!res.ok) {
        let title = "";
        let type: string | undefined;
        let unresolved: string[] | undefined;
        try {
            const body = (await res.json()) as { title?: unknown; type?: unknown; unresolved?: unknown };
            if (typeof body.title === "string") title = body.title;
            if (typeof body.type === "string") type = body.type;
            if (Array.isArray(body.unresolved)) unresolved = body.unresolved.filter((u): u is string => typeof u === "string");
        } catch {
            // No JSON body (403 / 404) — fall back to the generic message.
        }
        throw new ConnectApiError(title || `Connect BFF ${path} → ${res.status}`, res.status, type, unresolved);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

/**
 * PUT / DELETE of a meeting-notes block. A stale version comes back as 409
 * with the block as it is now, so the editor can reload just that block.
 */
async function sendNoteChange(path: string, token: string | undefined, init: RequestInit): Promise<NoteChangeResult> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
        res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers });
    } catch {
        return { ok: false, status: 0, message: "Couldn't reach the server. Check your connection." };
    }
    if (res.ok) {
        return res.status === 204 ? { ok: true } : { ok: true, block: (await res.json()) as MeetingNoteBlock };
    }
    let message = `Couldn't save (${res.status})`;
    let current: MeetingNoteBlock | undefined;
    try {
        const body = (await res.json()) as { title?: unknown; current?: MeetingNoteBlock };
        if (typeof body.title === "string" && body.title) message = body.title;
        if (body.current && typeof body.current.id === "string") current = body.current;
    } catch { /* no body */ }
    return { ok: false, status: res.status, message, current };
}

// ----- Meetings (Microsoft Teams via shared Fg.Meetings provider) -----

export interface CreateMeetingPayload {
    subject: string;
    /** ISO-8601 with offset, e.g. "2026-04-30T10:30:00.000Z". */
    startUtc: string;
    /** ISO-8601 with offset. */
    endUtc: string;
}

export interface CreateMeetingResponse {
    provider: string;
    joinUrl: string;
    providerMeetingId: string;
    conferenceId: string | null;
    dialInNumbers: string[];
    subject: string;
    startUtc: string;
    endUtc: string;
}

export const connectApi = {
    listTeams: (token: string | undefined) => fetchJson<Team[]>("/api/teams", token),
    createMeeting: (payload: CreateMeetingPayload, token: string | undefined) =>
        fetchJson<CreateMeetingResponse>("/api/meetings/teams", token, {
            method: "POST",
            body: JSON.stringify(payload)
        }),
    createTeam: (p: { slug: string; name: string; description?: string; visibility?: string }, token: string | undefined) =>
        fetchJson<Team>("/api/teams", token, { method: "POST", body: JSON.stringify(p) }),
    deleteTeam: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/teams/${id}`, token, { method: "DELETE" }),

    listChannels: (teamId: string, token: string | undefined) =>
        fetchJson<Channel[]>(`/api/teams/${teamId}/channels`, token),
    createChannel: (teamId: string, p: { name: string; description?: string; kind?: string }, token: string | undefined) =>
        fetchJson<Channel>(`/api/teams/${teamId}/channels`, token, { method: "POST", body: JSON.stringify(p) }),

    listMessages: (channelId: string, token: string | undefined) =>
        fetchJson<ChatMessage[]>(`/api/channels/${channelId}/messages`, token),
    sendMessage: (
        channelId: string,
        bodyHtml: string,
        token: string | undefined,
        importance?: MessageImportance,
        post?: { subject?: string | null; isAnnouncement?: boolean }
    ) =>
        fetchProblem<ChatMessage>(`/api/channels/${channelId}/messages`, token, {
            method: "POST",
            body: JSON.stringify({
                bodyHtml,
                importance,
                subject: post?.subject || undefined,
                isAnnouncement: post?.isAnnouncement || undefined
            })
        }),
    reactMessage: (channelId: string, messageId: string, emoji: string, token: string | undefined) =>
        fetchJson<void>(`/api/channels/${channelId}/messages/${messageId}/reactions`, token, {
            method: "POST",
            body: JSON.stringify({ emoji })
        }),
    pinMessage: (channelId: string, messageId: string, pin: boolean, token: string | undefined) =>
        fetchJson<void>(
            `/api/channels/${channelId}/messages/${messageId}/${pin ? "pin" : "unpin"}`,
            token,
            { method: "POST" }
        ),
    deleteMessage: (channelId: string, messageId: string, token: string | undefined) =>
        fetchJson<void>(`/api/channels/${channelId}/messages/${messageId}`, token, { method: "DELETE" }),
    editMessage: (channelId: string, messageId: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<ChatMessage>(`/api/channels/${channelId}/messages/${messageId}`, token, {
            method: "PUT",
            body: JSON.stringify({ bodyHtml })
        }),

    listDirectChats: (token: string | undefined) => fetchJson<DirectChat[]>("/api/dms", token),
    startDirectChat: (otherUserId: string, token: string | undefined) =>
        fetchJson<DirectChat>("/api/dms", token, { method: "POST", body: JSON.stringify({ otherUserId }) }),
    listDirectMessages: (chatId: string, token: string | undefined) =>
        fetchJson<ChatMessage[]>(`/api/dms/${chatId}/messages`, token),
    sendDirectMessage: (chatId: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<ChatMessage>(`/api/dms/${chatId}/messages`, token, {
            method: "POST",
            body: JSON.stringify({ bodyHtml })
        })
};

export const connectHubUrl = new URL("/hubs/connect", baseUrl).toString();

/**
 * Mirrors `ConnectIdentity.Normalize` on the BFF — collapses dashed GUIDs
 * to the canonical no-dash lower-case hex form. The OIDC `sub` claim is
 * dashed; the BFF stores DM participants no-dash. Comparing the two
 * directly means "me" never matches and the user shows up in their own
 * roster / opens DMs with themselves.
 */
export function normalizeUserId(userId: string | undefined | null): string {
    if (!userId) return "";
    const hex = userId.replace(/[{}\-]/g, "").toLowerCase();
    return /^[0-9a-f]{32}$/.test(hex) ? hex : userId.toLowerCase();
}

// ---------- Parity surface: threads, mentions, notifications, DMs, polls, meetings ----------

/** What one person wants from one channel (keys of the map are channel ids without dashes). */
export interface ChannelNotifyPrefs {
    /** Every new post shows in Activity. */
    posts: boolean;
    /** Replies to your posts and to threads you replied in. */
    replies: boolean;
    /** @channel / @everyone. */
    mentions: boolean;
    /** Also show a banner (off: Activity only). */
    banner: boolean;
}

export interface Notification {
    id: string;
    type: string;
    message: string;
    linkUrl: string | null;
    isRead: boolean;
    createdAt: string;
}

export interface Conversation {
    id: string;
    name: string | null;
    createdByUserId: string;
    createdAt: string;
}

export interface SearchHit {
    kind: "channel-message" | "direct-message" | "conversation-message";
    id: string;
    containerId: string;
    author: string;
    bodyHtml: string;
    sentAt: string;
}

export interface ConversationMember {
    id: string;
    userId: string;
    email: string;
    displayName: string | null;
    lastReadAt: string | null;
}

export interface ConversationMessage {
    id: string;
    conversationId: string;
    authorUserId: string;
    authorEmail: string;
    authorDisplayName: string | null;
    bodyHtml: string;
    isEdited: boolean;
    sentAt: string;
    editedAt: string | null;
}

/** The chat kinds with per-message edit/delete/reactions besides channels (the BFF route prefix). */
export type ChatScope = "dms" | "conversations" | "meetings";

/** One (message, emoji) reaction summary in a DM or group chat. */
export interface ChatReaction {
    messageId: string;
    emoji: string;
    count: number;
    userIds: string[];
    firstReactedAt: string;
}

/** One row of the unified Chat list (1:1 chats, group chats and meeting chats together). */
export interface ChatListItem {
    /** "meeting": a meeting's chat — `id` is the meeting id, `title` is "Meeting: <subject>". */
    kind: "dm" | "group" | "meeting";
    id: string;
    /** The other person's name, or the group's name (or its members'). */
    title: string;
    peerUserId: string | null;
    peerEmail: string | null;
    memberCount: number;
    lastMessage: {
        authorUserId: string;
        authorName: string | null;
        snippet: string;
        sentAt: string;
        mine: boolean;
        /** A system line ("Priya added Alex"): shown without the sender. */
        system?: boolean;
    } | null;
    unreadCount: number;
    lastActivityAt: string;
    memberNames: string[];
    /** Unread messages that @mention me — the "@" badge. Absent on older servers. */
    mentionCount?: number;
}

/** One person's presence from the snapshot; the status-message fields are absent when none is set. */
export interface PresenceEntry {
    userId: string;
    status: string;
    statusMessage?: string | null;
    statusEmoji?: string | null;
    statusExpiresAt?: string | null;
    showWhenMessaged?: boolean | null;
}

/** My own status message and "Appear offline" (GET /api/presence/me). */
export interface MyStatus {
    message?: string | null;
    emoji?: string | null;
    expiresAt?: string | null;
    showWhenMessaged?: boolean;
    appearOffline?: boolean;
}

/** The conversation kinds that have a Files tab (the BFF route prefix). */
export type FilesScope = "dms" | "conversations" | "channels" | "meetings";

/** A page of a conversation's files: messages carrying Drive markers, plus old channel attachments. */
export interface ConversationFilesPage {
    messages: Array<{
        messageId: string;
        authorUserId: string;
        authorEmail: string;
        authorDisplayName?: string | null;
        /** Only the message's file-marker tags — parse with splitFileMarkers. */
        markersHtml: string;
        sentAt: string;
    }>;
    legacy: Array<{
        id: string;
        messageId: string;
        fileName: string;
        contentType: string;
        sizeBytes: number;
        uploadedAt: string;
        authorUserId: string;
        authorEmail: string;
        authorDisplayName?: string | null;
    }>;
    /** Pass back as `before` for older files; absent once the whole history was scanned. */
    nextBefore?: string | null;
}

export interface Poll {
    id: string;
    question: string;
    allowMultiple: boolean;
    createdAt: string;
    closedAt: string | null;
    options: PollOption[];
}

export interface PollOption {
    id: string;
    label: string;
    voteCount: number;
}

export type MeetingKind = "audio" | "video";
/** Who waits in the lobby: nobody, guests only (default), or everyone not invited. */
export type LobbyPolicy = "everyone" | "organization" | "invited";

export interface Meeting {
    id: string;
    subject: string;
    hostUserId: string;
    kind?: MeetingKind;
    /** An ad-hoc call (rings invitees) rather than a scheduled meeting. */
    isCall?: boolean;
    lobbyPolicy?: LobbyPolicy;
    teamId: string | null;
    channelId: string | null;
    scheduledStart: string;
    scheduledEnd: string | null;
    status: "Scheduled" | "Live" | "Ended" | "Cancelled";
    externalProvider?: string | null;
    externalJoinUrl?: string | null;
    actualStart?: string | null;
    actualEnd?: string | null;
    /** Agenda / notes. */
    description?: string | null;
}

export interface RecordingSummary {
    id: string;
    meetingId: string;
    capturedByDisplayName: string;
    sizeBytes: number;
    duration: string; // .NET TimeSpan serializes as "hh:mm:ss"
    createdAt: string;
}

/** A meeting recording stored in the host's Drive and shared with the attendees there. */
export interface MeetingDriveRecording {
    id: string;
    meetingId: string;
    driveItemId: string;
    fileName: string;
    sizeBytes: number;
    durationSeconds: number;
    recordedBy: string;
    recordedByName?: string | null;
    createdAt: string;
}

/** One stored message in a meeting's chat. Guests' author ids are "guest:<connection>". */
export interface MeetingChatMessage {
    id: string;
    meetingId: string;
    authorUserId: string;
    authorEmail: string;
    authorDisplayName?: string | null;
    isGuest: boolean;
    bodyHtml: string;
    isEdited: boolean;
    sentAt: string;
    editedAt?: string | null;
}

/** Someone in a meeting: the organizer, an invitee or someone who joined. `userId` is "" when only an email is known. */
export interface MeetingChatMember {
    userId: string;
    email: string;
    displayName?: string | null;
    role: "organizer" | "invitee" | "participant";
}

/**
 * One thing on a meeting whiteboard (see MeetingWhiteboardStore on the BFF).
 * pen / highlighter: points = x0,y0,x1,y1,…; rect / ellipse / arrow / line:
 * points = x1,y1,x2,y2; text / note: x, y (top-left), w / h, text. `size` is
 * the stroke width, or the font size for text and notes. The server sends
 * null for fields a kind doesn't use.
 */
export interface WhiteboardObject {
    id: string;
    kind: "pen" | "highlighter" | "rect" | "ellipse" | "arrow" | "line" | "text" | "note";
    color: string;
    size: number;
    points?: number[] | null;
    x?: number | null;
    y?: number | null;
    w?: number | null;
    h?: number | null;
    text?: string | null;
    /** Server-stamped author (user id, or guest:<connection>). */
    author?: string | null;
    authorName?: string | null;
}

export interface WhiteboardSnapshot {
    objects: WhiteboardObject[];
    locked: boolean;
    leader?: { connectionId: string; displayName: string } | null;
    viewers: number;
    revision: number;
    updatedAt?: string | null;
    /** True while the meeting's board is open in memory (the room is using it). */
    live: boolean;
}

export type MeetingNoteKind = "agenda" | "note" | "action";

/** One block of a meeting's notes: an agenda item, a notes section or an action item. */
export interface MeetingNoteBlock {
    id: string;
    meetingId: string;
    kind: MeetingNoteKind;
    position: number;
    text: string;
    assigneeUserId?: string | null;
    assigneeName?: string | null;
    /** "yyyy-mm-dd". */
    dueDate?: string | null;
    done: boolean;
    /** Send back with every edit — a stale one is refused with 409. */
    version: number;
    createdBy: string;
    createdByName?: string | null;
    updatedBy: string;
    updatedByName?: string | null;
    createdAt: string;
    updatedAt: string;
}

export type NoteChangeResult =
    | { ok: true; block?: MeetingNoteBlock }
    | { ok: false; status: number; message: string; current?: MeetingNoteBlock };

export interface AttendanceSession {
    joinedAt: string;
    /** Absent while they're still in the room. */
    leftAt?: string | null;
    live: boolean;
}

/** One person on the attendance report (guests are grouped by name). */
export interface AttendanceRow {
    key: string;
    userId?: string | null;
    email?: string | null;
    displayName: string;
    isGuest: boolean;
    isOrganizer: boolean;
    invited: boolean;
    firstJoinedAt: string;
    lastLeftAt?: string | null;
    totalSeconds: number;
    sessions: AttendanceSession[];
}

/** Teams' meeting Recap: attendance and duration (recordings, files and chat load from their own routes). */
export interface MeetingRecap {
    meeting: Meeting;
    organizerUserId: string;
    organizerName?: string | null;
    organizerEmail?: string | null;
    isOrganizer: boolean;
    /** First person in. */
    firstJoinAt?: string | null;
    /** Last person out; absent while someone is still there. */
    lastLeaveAt?: string | null;
    durationSeconds: number;
    attendance: AttendanceRow[];
    messageCount: number;
}

export interface TranscriptLine {
    id: string;
    meetingId: string;
    speakerUserId: string;
    speakerDisplayName: string;
    text: string;
    spokenAt: string;
}

export interface ChannelTabSummary {
    id: string;
    channelId: string;
    name: string;
    url: string;
    orderIndex: number;
    createdAt: string;
}

export interface WebhookSummary {
    id: string;
    channelId: string;
    name: string;
    token: string;
    isRevoked: boolean;
    callCount: number;
    createdAt: string;
    lastUsedAt: string | null;
}

export interface UnfurlPreview {
    url: string;
    title: string | null;
    description: string | null;
    image: string | null;
    siteName: string | null;
}

export interface TranslateResult {
    translated: string | null;
    provider: string;
    fromCache: boolean;
}

export interface SavedMessageSummary {
    id: string;
    containerKind: "channel" | "dm" | "conversation";
    containerId: string;
    messageId: string;
    authorDisplayName: string;
    authorEmail: string;
    bodyHtml: string;
    sentAt: string;
    savedAt: string;
}

export interface ConnectPolicy {
    tenantId: string;
    allowRecording: boolean;
    allowFileSharing: boolean;
    allowIncomingWebhooks: boolean;
    allowChannelTabs: boolean;
    allowFederation: boolean;
}

export interface UserPreferences {
    userId: string;
    /** Minutes since midnight in the user's local zone (0-1439). null = DND off. */
    dndStartMinutes: number | null;
    dndEndMinutes: number | null;
    /** Bitmask: Sun=1, Mon=2, ..., Sat=64. 0 = every day. */
    dndDaysMask: number;
    timeZoneId: string | null;
}

export const connectParityApi = {
    // Threads
    listThreadReplies: (channelId: string, messageId: string, token: string | undefined) =>
        fetchJson<ChatMessage[]>(`/api/channels/${channelId}/messages/${messageId}/replies`, token),
    sendThreadReply: (channelId: string, messageId: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<ChatMessage>(`/api/channels/${channelId}/messages/${messageId}/replies`, token, {
            method: "POST",
            body: JSON.stringify({ bodyHtml })
        }),
    parseMentions: (channelId: string, messageId: string, token: string | undefined) =>
        fetchJson<{ parsed: number }>(`/api/channels/${channelId}/messages/${messageId}/mentions`, token, {
            method: "POST",
            body: JSON.stringify({ notifyAll: true })
        }),

    // Follow / unfollow a channel thread (replies to followed threads show in Activity).
    getThreadFollow: (messageId: string, token: string | undefined) =>
        fetchJson<{ following: boolean }>(`/api/preferences/threads/${messageId}`, token),
    setThreadFollow: (messageId: string, following: boolean, token: string | undefined) =>
        fetchJson<{ following: boolean }>(`/api/preferences/threads/${messageId}`, token, { method: "PUT", body: JSON.stringify({ following }) }),

    // Per-channel notification choices (Activity entries and banners).
    getChannelNotify: (token: string | undefined) =>
        fetchJson<Record<string, ChannelNotifyPrefs>>("/api/preferences/channels", token),
    setChannelNotify: (channelId: string, prefs: ChannelNotifyPrefs, token: string | undefined) =>
        fetchJson<ChannelNotifyPrefs>(`/api/preferences/channels/${channelId}`, token, { method: "PUT", body: JSON.stringify(prefs) }),

    // Notifications
    listNotifications: (unreadOnly: boolean, token: string | undefined) =>
        fetchJson<Notification[]>(`/api/notifications?unreadOnly=${unreadOnly}`, token),
    markNotificationRead: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/notifications/${id}/read`, baseUrl).toString(), {
            method: "POST", headers
        });
        if (!res.ok) throw new Error(`mark read → ${res.status}`);
    },
    markAllNotificationsRead: (token: string | undefined) =>
        fetchJson<{ marked: number }>("/api/notifications/read-all", token, { method: "POST" }),

    // 1:1 DMs — shared store with mail-web's chat dock. Mail ↔ Connect
    // round-trips through these endpoints. Methods are named `DirectChat*`
    // for hooks.ts clarity (distinguishes 1:1 from group conversations).
    listDirectChats: (token: string | undefined) =>
        fetchJson<DirectChat[]>("/api/dms", token),
    startDirectChat: (otherUserId: string, token: string | undefined) =>
        fetchJson<DirectChat>("/api/dms", token, {
            method: "POST",
            body: JSON.stringify({ otherUserId })
        }),
    /** `around`: the messages either side of one (jumping to a search hit or pin). */
    listDirectChatMessages: (id: string, token: string | undefined, around?: string | null) =>
        fetchJson<ChatMessage[]>(`/api/dms/${id}/messages${around ? `?around=${encodeURIComponent(around)}&take=100` : ""}`, token),
    sendDirectChatMessage: (id: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<ChatMessage>(`/api/dms/${id}/messages`, token, {
            method: "POST",
            body: JSON.stringify({ bodyHtml })
        }),

    // Global search across channels, 1:1 DMs, and group conversations.
    // Backend returns a unified SearchHit list ordered by recency.
    searchAll: (q: string, take: number, token: string | undefined) =>
        fetchJson<SearchHit[]>(
            `/api/search?q=${encodeURIComponent(q)}&take=${take}`,
            token
        ),

    // Presence snapshot — same shape as mail-web consumes. userIds come
    // back no-dash (normalized) per the hub's OnConnectedAsync fix.
    listPresence: (token: string | undefined) =>
        fetchJson<PresenceEntry[]>("/api/presence", token),
    /** My status message + "Appear offline". */
    getMyStatus: (token: string | undefined) =>
        fetchJson<MyStatus>("/api/presence/me", token),
    /** Set my status message (an empty message clears it). `expiresAt` null = never clear. */
    setStatusMessage: (payload: { message: string; emoji?: string | null; expiresAt?: string | null; showWhenMessaged: boolean }, token: string | undefined) =>
        fetchJson<MyStatus>("/api/presence/me/status", token, { method: "PUT", body: JSON.stringify(payload) }),
    /** "Appear offline": others see Offline while I keep working. */
    setAppearOffline: (appearOffline: boolean, token: string | undefined) =>
        fetchJson<MyStatus>("/api/presence/me/appear-offline", token, { method: "PUT", body: JSON.stringify({ appearOffline }) }),

    /** The Files tab: files shared in a 1:1 chat, group chat or channel, newest first. */
    listConversationFiles: (scope: FilesScope, id: string, before: string | null, token: string | undefined) =>
        fetchJson<ConversationFilesPage>(
            `/api/${scope}/${encodeURIComponent(id)}/files${before ? `?before=${encodeURIComponent(before)}` : ""}`,
            token),
    /** Authenticated download URL of an old (pre-Drive) channel attachment — fetch it with the bearer token. */
    legacyAttachmentUrl: (channelId: string, messageId: string, attachmentId: string): string =>
        new URL(`/api/channels/${channelId}/messages/${messageId}/attachments/${attachmentId}/download`, baseUrl).toString(),

    // Directory — used by Connect's Chats view to pick someone new.
    searchDirectory: (q: string, token: string | undefined) =>
        fetchJson<Array<{ userId: string; email: string; displayName: string | null; tenantId: string }>>(
            `/api/directory/users?q=${encodeURIComponent(q)}&take=10`,
            token
        ),
    lookupDirectoryUser: (userId: string, token: string | undefined) =>
        fetchJson<{ userId: string; email: string; displayName: string | null; tenantId: string }>(
            `/api/directory/users/${encodeURIComponent(userId)}`,
            token
        ),
    listTenantUsers: (token: string | undefined, take = 200) =>
        fetchJson<Array<{ userId: string; email: string; displayName: string | null; tenantId: string }>>(
            `/api/directory/tenant?take=${take}`,
            token
        ),

    // Conversations (group chats)
    listConversations: (token: string | undefined) =>
        fetchJson<Conversation[]>("/api/conversations", token),
    createConversation: (
        payload: { name?: string; participants: { userId: string; email: string; displayName?: string }[] },
        token: string | undefined
    ) => fetchJson<Conversation>("/api/conversations", token, {
        method: "POST", body: JSON.stringify(payload)
    }),
    /** Group chats: name the chat (empty clears it), add people, remove someone or leave (remove yourself). Each leaves a line in the chat. */
    renameConversation: (id: string, name: string, token: string | undefined) =>
        fetchJson<Conversation>(`/api/conversations/${id}`, token, { method: "PATCH", body: JSON.stringify({ name }) }),
    addConversationMembersWithHistory: (id: string, participants: { userId: string; email: string; displayName?: string }[], includeHistory: boolean, token: string | undefined) =>
        fetchJson<{ added: number }>(`/api/conversations/${id}/members`, token, { method: "POST", body: JSON.stringify({ participants, includeHistory }) }),
    removeConversationMember: (id: string, userId: string, token: string | undefined) =>
        fetchJson<void>(`/api/conversations/${id}/members/${encodeURIComponent(userId)}`, token, { method: "DELETE" }),
    listConversationMembers: (id: string, token: string | undefined) =>
        fetchJson<ConversationMember[]>(`/api/conversations/${id}/members`, token),
    /** Teams' "Add people" to a group chat (FPT-8). Returns the members after the change. */
    addConversationMembers: (id: string, userIds: string[], token: string | undefined) =>
        fetchProblem<ConversationMember[]>(`/api/conversations/${id}/members`, token, {
            method: "POST", body: JSON.stringify({ userIds })
        }),
    /**
     * FPT-8: the chat with exactly these people (by email) — their 1:1 chat for
     * one, the group chat with all of them (or a new one named `topic`) for
     * several. Used by the /start links other apps (Mail) open.
     */
    openChat: (emails: string[], topic: string | null, token: string | undefined) =>
        fetchProblem<OpenChatResult>("/api/chats/open", token, {
            method: "POST", body: JSON.stringify({ with: emails, topic: topic ?? undefined })
        }),
    listConversationMessages: (id: string, token: string | undefined, around?: string | null) =>
        fetchJson<ConversationMessage[]>(
            `/api/conversations/${id}/messages${around ? `?around=${encodeURIComponent(around)}&take=100` : ""}`, token),
    sendConversationMessage: (id: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<ConversationMessage>(`/api/conversations/${id}/messages`, token, {
            method: "POST", body: JSON.stringify({ bodyHtml })
        }),

    // DM / group-chat message actions — the author edits or deletes, anyone in the chat reacts.
    editChatMessage: (scope: ChatScope, containerId: string, messageId: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<unknown>(`/api/${scope}/${containerId}/messages/${messageId}`, token, {
            method: "PUT", body: JSON.stringify({ bodyHtml })
        }),
    deleteChatMessage: (scope: ChatScope, containerId: string, messageId: string, token: string | undefined) =>
        fetchJson<void>(`/api/${scope}/${containerId}/messages/${messageId}`, token, { method: "DELETE" }),
    listChatReactions: (scope: ChatScope, containerId: string, token: string | undefined) =>
        fetchJson<ChatReaction[]>(`/api/${scope}/${containerId}/reactions`, token),
    reactChatMessage: (scope: ChatScope, containerId: string, messageId: string, emoji: string, token: string | undefined) =>
        fetchJson<void>(`/api/${scope}/${containerId}/messages/${messageId}/reactions`, token, {
            method: "POST", body: JSON.stringify({ emoji })
        }),
    unreactChatMessage: (scope: ChatScope, containerId: string, messageId: string, emoji: string, token: string | undefined) =>
        fetchJson<void>(
            `/api/${scope}/${containerId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
            token,
            { method: "DELETE" }
        ),
    /** Every 1:1 and group chat, most recent first, with preview and unread count. */
    listChats: (token: string | undefined) => fetchJson<ChatListItem[]>("/api/chats", token),
    /** Mark a DM read (group chats use markConversationRead). */
    markDmRead: (chatId: string, token: string | undefined) =>
        fetchJson<{ at: string }>(`/api/dms/${chatId}/read`, token, { method: "POST" }),
    markConversationRead: (id: string, token: string | undefined) =>
        fetchJson<{ at: string }>(`/api/conversations/${id}/read`, token, { method: "POST" }),

    // Read receipts (channels)
    markChannelRead: (channelId: string, token: string | undefined) =>
        fetchJson<{ at: string }>(`/api/channels/${channelId}/read`, token, { method: "POST" }),

    // Polls
    listPolls: (channelId: string, token: string | undefined) =>
        fetchJson<Poll[]>(`/api/channels/${channelId}/polls`, token),
    createPoll: (channelId: string, payload: { question: string; options: string[]; allowMultiple: boolean }, token: string | undefined) =>
        fetchJson<{ id: string; question: string; allowMultiple: boolean }>(`/api/channels/${channelId}/polls`, token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    votePoll: async (channelId: string, pollId: string, optionIds: string[], token: string | undefined) => {
        const headers = new Headers({ "Content-Type": "application/json" });
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/channels/${channelId}/polls/${pollId}/vote`, baseUrl).toString(), {
            method: "POST", headers, body: JSON.stringify({ optionIds })
        });
        if (!res.ok) throw new Error(`vote → ${res.status}`);
    },
    closePoll: async (channelId: string, pollId: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/channels/${channelId}/polls/${pollId}/close`, baseUrl).toString(), {
            method: "POST", headers
        });
        if (!res.ok) throw new Error(`close → ${res.status}`);
    },

    // Meetings
    listMeetings: (token: string | undefined) =>
        fetchJson<Meeting[]>("/api/meetings", token),
    /** Anonymous meeting summary for the guest-join landing page. */
    publicMeeting: (id: string) =>
        fetchJson<{ id: string; subject: string; status: string; joinable: boolean; kind?: MeetingKind; scheduledEnd?: string | null }>(
            `/api/meetings/${encodeURIComponent(id)}/public`, undefined),
    /** Full meeting (host, kind, call, lobby policy) for a signed-in participant. */
    getMeeting: (id: string, token: string | undefined) =>
        fetchJson<Meeting>(`/api/meetings/${encodeURIComponent(id)}`, token),
    createMeeting: (payload: {
        subject: string;
        scheduledStart: string;
        scheduledEnd?: string;
        channelId?: string;
        teamId?: string;
        invitees?: string[];
        description?: string;
        kind?: MeetingKind;
        /** Ad-hoc call: invitees ring now and no calendar email is sent. */
        isCall?: boolean;
        lobbyPolicy?: LobbyPolicy;
    }, token: string | undefined) =>
        fetchJson<Meeting>("/api/meetings", token, { method: "POST", body: JSON.stringify(payload) }),
    /** Decline a ringing call — the caller hears about it and other tabs stop ringing. */
    declineCall: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/meetings/${encodeURIComponent(id)}/decline`, token, { method: "POST" }),
    /** End a meeting for everyone (host only) — also stops an unanswered call ringing. */
    endMeeting: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/meetings/${encodeURIComponent(id)}/end`, token, { method: "POST" }),
    /** STUN/TURN for this meeting's peer connections; anonymous so guests get the relay too. */
    iceServers: (id: string) =>
        fetchJson<Array<{ urls: string[]; username?: string | null; credential?: string | null }>>(
            `/api/meetings/${encodeURIComponent(id)}/ice-servers`, undefined),
    /** Who a meeting or call was sent to — for the caller's "Calling…" screen. */
    meetingInvitees: (id: string, token: string | undefined) =>
        fetchJson<Array<{ id: string; email: string; displayName: string | null; response: string }>>(
            `/api/meetings/${encodeURIComponent(id)}/attendees`, token),
    /** Everyone who has joined the meeting (signed-in people only). */
    meetingParticipants: (id: string, token: string | undefined) =>
        fetchJson<Array<{ id: string; userId: string; email: string; displayName: string | null; joinedAt: string; leftAt: string | null }>>(
            `/api/meetings/${encodeURIComponent(id)}/participants`, token),
    /** Recordings saved to the host's Drive for this meeting (organizer, invitees and participants). */
    listMeetingRecordings: (id: string, token: string | undefined) =>
        fetchJson<MeetingDriveRecording[]>(`/api/meetings/${encodeURIComponent(id)}/recordings`, token),
    // Meeting chat — stored, and readable by the organizer, invitees and anyone who joined.
    // Edit / delete / reactions go through the ChatScope "meetings" actions above.
    listMeetingMessages: (id: string, token: string | undefined, around?: string | null) =>
        fetchJson<MeetingChatMessage[]>(
            `/api/meetings/${encodeURIComponent(id)}/messages${around ? `?around=${encodeURIComponent(around)}` : ""}`, token),
    sendMeetingMessage: (id: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<MeetingChatMessage>(`/api/meetings/${encodeURIComponent(id)}/messages`, token, {
            method: "POST", body: JSON.stringify({ bodyHtml })
        }),
    markMeetingChatRead: (id: string, token: string | undefined) =>
        fetchJson<{ at: string }>(`/api/meetings/${encodeURIComponent(id)}/read`, token, { method: "POST" }),
    /** Organizer, invitees and people who joined — for @mentions and sharing files. */
    meetingChatMembers: (id: string, token: string | undefined) =>
        fetchJson<MeetingChatMember[]>(`/api/meetings/${encodeURIComponent(id)}/members`, token),
    /** Recap: duration and the attendance list. */
    meetingRecap: (id: string, token: string | undefined) =>
        fetchJson<MeetingRecap>(`/api/meetings/${encodeURIComponent(id)}/recap`, token),
    /** Authenticated attendance report (organizer only) — fetch it with the bearer token. */
    meetingAttendanceCsvUrl: (id: string): string =>
        new URL(`/api/meetings/${encodeURIComponent(id)}/attendance.csv`, baseUrl).toString(),
    /** Register a recording the host just saved to Drive (host only). */
    addMeetingRecording: (id: string, payload: { driveItemId: string; fileName: string; sizeBytes: number; durationSeconds: number }, token: string | undefined) =>
        fetchJson<MeetingDriveRecording>(`/api/meetings/${encodeURIComponent(id)}/recordings`, token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    /** The meeting's whiteboard, read-only (the live board while the room has it open, else the saved one). */
    meetingWhiteboard: (id: string, token: string | undefined) =>
        fetchJson<WhiteboardSnapshot>(`/api/meetings/${encodeURIComponent(id)}/whiteboard`, token),
    // Meeting notes — agenda, notes and action items; same access as the meeting chat.
    meetingNotes: (id: string, token: string | undefined) =>
        fetchJson<MeetingNoteBlock[]>(`/api/meetings/${encodeURIComponent(id)}/notes`, token),
    createMeetingNote: (id: string, payload: { kind: MeetingNoteKind; text: string; assigneeUserId?: string | null; dueDate?: string | null; done?: boolean }, token: string | undefined) =>
        fetchProblem<MeetingNoteBlock>(`/api/meetings/${encodeURIComponent(id)}/notes`, token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    /** Full replace of the editable fields, based on `version`; 409 → `current`. */
    updateMeetingNote: (id: string, blockId: string, payload: { version: number; text: string; assigneeUserId?: string | null; dueDate?: string | null; done: boolean }, token: string | undefined) =>
        sendNoteChange(`/api/meetings/${encodeURIComponent(id)}/notes/${encodeURIComponent(blockId)}`, token, {
            method: "PUT", body: JSON.stringify(payload)
        }),
    deleteMeetingNote: (id: string, blockId: string, version: number, token: string | undefined) =>
        sendNoteChange(`/api/meetings/${encodeURIComponent(id)}/notes/${encodeURIComponent(blockId)}?version=${version}`, token, {
            method: "DELETE"
        }),
    /** Answer a meeting invitation (Teams' RSVP). */
    rsvpMeeting: (id: string, response: "Accepted" | "Tentative" | "Declined", token: string | undefined) =>
        fetchJson<void>(`/api/meetings/${encodeURIComponent(id)}/rsvp`, token, {
            method: "POST", body: JSON.stringify({ response })
        }),
    /** Reschedule / edit (host only) — attendees get an updated invitation. */
    updateMeeting: (id: string, payload: { subject?: string; scheduledStart?: string; scheduledEnd?: string; description?: string }, token: string | undefined) =>
        fetchJson<Meeting>(`/api/meetings/${encodeURIComponent(id)}`, token, { method: "PUT", body: JSON.stringify(payload) }),
    /** Cancel (host only) — attendees get a cancellation that removes it from their calendar. */
    cancelMeeting: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/meetings/${encodeURIComponent(id)}/cancel`, baseUrl).toString(), { method: "POST", headers });
        if (!res.ok) throw new Error(`cancel → ${res.status}`);
    },
    startMeeting: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        await fetch(new URL(`/api/meetings/${id}/start`, baseUrl).toString(), { method: "POST", headers });
    },
    joinMeeting: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        await fetch(new URL(`/api/meetings/${id}/join`, baseUrl).toString(), { method: "POST", headers });
    },
    leaveMeeting: async (id: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        await fetch(new URL(`/api/meetings/${id}/leave`, baseUrl).toString(), { method: "POST", headers });
    },
    inviteToMeeting: (meetingId: string, emails: string[], token: string | undefined) =>
        fetchJson<void>(`/api/meetings/${meetingId}/invite`, token, {
            method: "POST",
            body: JSON.stringify({ emails })
        }),

    // Recordings — client captures via MediaRecorder, uploads via /api/uploads,
    // then calls this to register metadata linking the object key to a meeting.
    registerRecording: async (payload: {
        meetingId: string;
        objectKey: string;
        contentType: string;
        sizeBytes: number;
        durationSeconds: number;
        startedAt: string;
        endedAt: string;
    }, token: string | undefined) => {
        const headers = new Headers({ "Content-Type": "application/json" });
        if (token) headers.set("Authorization", `Bearer ${token}`);
        await fetch(new URL("/api/recordings", baseUrl).toString(), {
            method: "POST",
            headers,
            body: JSON.stringify(payload)
        });
    },
    listRecordings: async (meetingId: string, token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL(`/api/recordings/meeting/${meetingId}`, baseUrl).toString(), { headers });
        if (!res.ok) return [];
        return (await res.json()) as RecordingSummary[];
    },
    /** Authenticated download URL for a recording. Caller should fetch with Authorization header to stream the .webm file. */
    recordingDownloadUrl: (recordingId: string): string =>
        new URL(`/api/recordings/${recordingId}/download`, baseUrl).toString(),

    /** Authenticated URL for the meeting's .ics file (RFC 5545 VEVENT). */
    meetingIcsUrl: (meetingId: string): string =>
        new URL(`/api/meetings/${meetingId}/ics`, baseUrl).toString(),

    // Tenant feature policy snapshot
    getPolicy: (token: string | undefined) =>
        fetchJson<ConnectPolicy>("/api/policy", token),

    /** Server-side OpenGraph link unfurl. */
    unfurl: (url: string, token: string | undefined) =>
        fetchJson<UnfurlPreview>(`/api/unfurl?url=${encodeURIComponent(url)}`, token),

    /** Server-side translation (LibreTranslate-backed; null fallback when unconfigured). */
    translate: (text: string, targetLang: string, token: string | undefined) =>
        fetchJson<TranslateResult>("/api/translate", token, {
            method: "POST",
            body: JSON.stringify({ text, targetLang })
        }),

    // Saved / bookmarked messages
    listSaved: (token: string | undefined) =>
        fetchJson<SavedMessageSummary[]>("/api/saved/messages", token),
    saveChannelMessage: (channelId: string, messageId: string, token: string | undefined) =>
        fetchJson<SavedMessageSummary>(
            `/api/channels/${channelId}/messages/${messageId}/save`, token, { method: "POST" }),
    saveDirectMessage: (chatId: string, messageId: string, token: string | undefined) =>
        fetchJson<SavedMessageSummary>(
            `/api/dms/${chatId}/messages/${messageId}/save`, token, { method: "POST" }),
    saveConversationMessage: (conversationId: string, messageId: string, token: string | undefined) =>
        fetchJson<SavedMessageSummary>(
            `/api/conversations/${conversationId}/messages/${messageId}/save`, token, { method: "POST" }),
    unsaveMessage: (savedId: string, token: string | undefined) =>
        fetchJson<void>(`/api/saved/messages/${savedId}`, token, { method: "DELETE" }),

    // User preferences (DND quiet-hours, etc.)
    getPreferences: (token: string | undefined) =>
        fetchJson<UserPreferences>("/api/preferences", token),
    upsertPreferences: (payload: Partial<UserPreferences>, token: string | undefined) =>
        fetchJson<UserPreferences>("/api/preferences", token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),

    // Channel tabs
    listChannelTabs: (channelId: string, token: string | undefined) =>
        fetchJson<ChannelTabSummary[]>(`/api/channels/${channelId}/tabs`, token),
    addChannelTab: (channelId: string, name: string, url: string, token: string | undefined) =>
        fetchJson<ChannelTabSummary>(`/api/channels/${channelId}/tabs`, token, {
            method: "POST",
            body: JSON.stringify({ name, url })
        }),
    deleteChannelTab: (channelId: string, tabId: string, token: string | undefined) =>
        fetchJson<void>(`/api/channels/${channelId}/tabs/${tabId}`, token, { method: "DELETE" }),

    // Incoming webhooks
    listWebhooks: (channelId: string, token: string | undefined) =>
        fetchJson<WebhookSummary[]>(`/api/channels/${channelId}/webhooks`, token),
    createWebhook: (channelId: string, name: string, token: string | undefined) =>
        fetchJson<WebhookSummary>(`/api/channels/${channelId}/webhooks`, token, {
            method: "POST",
            body: JSON.stringify({ name })
        }),
    revokeWebhook: (channelId: string, webhookId: string, token: string | undefined) =>
        fetchJson<void>(`/api/channels/${channelId}/webhooks/${webhookId}/revoke`, token, { method: "POST" }),
    deleteWebhook: (channelId: string, webhookId: string, token: string | undefined) =>
        fetchJson<void>(`/api/channels/${channelId}/webhooks/${webhookId}`, token, { method: "DELETE" }),
    /** Public ingest URL — caller hands this to the external service. */
    webhookIngestUrl: (token: string): string =>
        new URL(`/api/webhooks/${encodeURIComponent(token)}`, baseUrl).toString(),

    listTranscript: (meetingId: string, token: string | undefined) =>
        fetchJson<TranscriptLine[]>(`/api/meetings/${meetingId}/transcript`, token),
    appendTranscriptLine: (meetingId: string, text: string, spokenAt: string, token: string | undefined) =>
        fetchJson<TranscriptLine>(`/api/meetings/${meetingId}/transcript`, token, {
            method: "POST",
            body: JSON.stringify({ text, spokenAt })
        }),

    // Web Push / VAPID — client fetches the server's public VAPID key to
    // derive its subscription, then posts the subscription back so the
    // server can push to it later.
    getPushConfig: async (token: string | undefined) => {
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL("/api/push/vapid-public-key", baseUrl).toString(), { headers });
        if (!res.ok) return { key: "", enabled: false };
        return (await res.json()) as { key: string; enabled: boolean };
    },
    subscribePush: async (sub: PushSubscriptionJSON, userAgent: string, token: string | undefined) => {
        const keys = sub.keys ?? {};
        const headers = new Headers({ "Content-Type": "application/json" });
        if (token) headers.set("Authorization", `Bearer ${token}`);
        await fetch(new URL("/api/push/subscribe", baseUrl).toString(), {
            method: "POST",
            headers,
            body: JSON.stringify({
                endpoint: sub.endpoint ?? "",
                p256dh: keys.p256dh ?? "",
                auth: keys.auth ?? "",
                userAgent
            })
        });
    },

    // File upload + attachment register
    uploadFile: async (file: File, token: string | undefined) => {
        const form = new FormData();
        form.append("file", file);
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(new URL("/api/uploads", baseUrl).toString(), {
            method: "POST", headers, body: form
        });
        if (!res.ok) throw new Error(`upload → ${res.status}`);
        return (await res.json()) as {
            bucket: string;
            objectKey: string;
            fileName: string;
            contentType: string;
            sizeBytes: number;
        };
    },
    registerChannelAttachment: (
        channelId: string,
        messageId: string,
        payload: { fileName: string; contentType: string; sizeBytes: number; bucket: string; objectKey: string },
        token: string | undefined
    ) => fetchJson<{ id: string; fileName: string }>(
        `/api/channels/${channelId}/messages/${messageId}/attachments`,
        token,
        { method: "POST", body: JSON.stringify(payload) }
    ),
    listChannelAttachments: (channelId: string, messageId: string, token: string | undefined) =>
        fetchJson<Array<{ id: string; messageId: string; fileName: string; contentType: string; sizeBytes: number }>>(
            `/api/channels/${channelId}/messages/${messageId}/attachments`,
            token
        ),

    // Team and channel management (owners).
    updateTeam: (id: string, p: { slug: string; name: string; description?: string | null; visibility?: string }, token: string | undefined) =>
        fetchJson<Team>(`/api/teams/${id}`, token, { method: "PUT", body: JSON.stringify(p) }),
    updateChannel: (teamId: string, channelId: string, p: { name: string; description?: string | null }, token: string | undefined) =>
        fetchJson<Channel>(`/api/teams/${teamId}/channels/${channelId}`, token, { method: "PUT", body: JSON.stringify(p) }),
    deleteChannel: (teamId: string, channelId: string, token: string | undefined) =>
        fetchJson<void>(`/api/teams/${teamId}/channels/${channelId}`, token, { method: "DELETE" }),
    addChannelMember: (teamId: string, channelId: string, p: { userId: string; email: string; displayName?: string | null; role?: string }, token: string | undefined) =>
        fetchJson<ChannelMember>(`/api/teams/${teamId}/channels/${channelId}/members`, token, { method: "POST", body: JSON.stringify(p) }),
    removeChannelMember: (teamId: string, channelId: string, memberId: string, token: string | undefined) =>
        fetchJson<void>(`/api/teams/${teamId}/channels/${channelId}/members/${memberId}`, token, { method: "DELETE" }),

    // Channel members (for @mention autocomplete).
    listChannelMembers: (teamId: string, channelId: string, token: string | undefined) =>
        fetchJson<ChannelMember[]>(`/api/teams/${teamId}/channels/${channelId}/members`, token),

    // Reactions on a single channel message — grouped by emoji with counts.
    listMessageReactions: (channelId: string, messageId: string, token: string | undefined) =>
        fetchJson<ReactionSummary[]>(
            `/api/channels/${channelId}/messages/${messageId}/reactions`,
            token
        )
};

export interface ReactionSummary {
    emoji: string;
    count: number;
    userIds: string[];
}

// ---------- Channel posts, pins, in-conversation search, polls ----------

/** One top-level channel post with its thread summary (Teams "Posts" layout). */
export interface ChannelPost {
    message: ChatMessage;
    replyCount: number;
    lastReplyAt?: string | null;
    /** Newest reply by someone else — compared with the read time for "New replies". */
    lastReplyFromOthersAt?: string | null;
    /** Most recent distinct repliers, newest first (up to 3). */
    repliers: Array<{ userId: string; name: string }>;
}

export interface ChannelPostsPage {
    posts: ChannelPost[];
    hasOlder: boolean;
    /** `around` pages only: newer posts exist beyond this page. */
    hasNewer: boolean;
    /** When I last read the channel, before this visit. */
    myLastReadAt?: string | null;
    /** Team owner / channel moderator: may pin and post announcements. */
    canModerate: boolean;
}

export interface ChannelUnread {
    channelId: string;
    unread: number;
}

/** Conversation kinds with pins (BFF route prefix). */
export type PinScope = "channels" | "dms" | "conversations";

export interface PinnedMessage {
    messageId: string;
    /** Channel replies: the post the reply belongs to. */
    parentMessageId?: string | null;
    authorUserId: string;
    authorName: string;
    sentAt: string;
    /** Plain text — render as text. */
    snippet: string;
    pinnedBy: string;
    pinnedByName?: string | null;
    pinnedAt: string;
}

export interface PinList {
    pins: PinnedMessage[];
    /** Whether I may pin / unpin here (team owners only in channels). */
    canPin: boolean;
    max: number;
}

/** Conversation kinds with in-conversation search and polls (BFF route prefix). */
export type ConversationScopeKind = "channels" | "dms" | "conversations" | "meetings";

export interface ConversationSearchHit {
    messageId: string;
    /** Channel replies: open this post's thread. */
    parentMessageId?: string | null;
    authorUserId: string;
    authorName: string;
    sentAt: string;
    /** Plain text around the first match — render as text, never HTML. */
    snippet: string;
    subject?: string | null;
}

export interface ConversationSearchPage {
    hits: ConversationSearchHit[];
    /** Pass back as `before` to search older messages; absent once the whole history was scanned. */
    nextBefore?: string | null;
    scanned: number;
}

export interface PollOptionView {
    id: string;
    label: string;
    votes: number;
    /** Who chose it; absent for anonymous polls. */
    voters?: Array<{ userId: string; name?: string | null }> | null;
}

export interface PollView {
    id: string;
    conversationKind: "channel" | "dm" | "group" | "meeting";
    conversationId: string;
    question: string;
    allowMultiple: boolean;
    isAnonymous: boolean;
    createdAt: string;
    closesAt?: string | null;
    closedAt?: string | null;
    isClosed: boolean;
    createdByUserId: string;
    createdByName?: string | null;
    isCreator: boolean;
    /** People who voted (a multiple-choice voter counts once). */
    totalVoters: number;
    options: PollOptionView[];
    /** My current choice(s). */
    myVotes: string[];
}

export interface CreatePollPayload {
    question: string;
    options: string[];
    allowMultiple: boolean;
    isAnonymous: boolean;
    closesAt?: string | null;
    /** Posted as a message card — always true from this app. */
    inline: true;
}

export const connectPostsApi = {
    /** Channel posts, newest page first; `before` pages to older posts, `around` loads the posts around one. */
    listChannelPosts: (channelId: string, opts: { before?: string | null; around?: string | null; take?: number }, token: string | undefined) => {
        const qs = new URLSearchParams();
        qs.set("take", String(opts.take ?? 30));
        if (opts.before) qs.set("before", opts.before);
        else if (opts.around) qs.set("around", opts.around);
        return fetchProblem<ChannelPostsPage>(`/api/channels/${channelId}/posts?${qs.toString()}`, token);
    },
    /** Unread counts for my channels in a team. */
    channelUnread: (teamId: string, token: string | undefined) =>
        fetchJson<ChannelUnread[]>(`/api/teams/${teamId}/channels/unread`, token),

    listPins: (scope: PinScope, id: string, token: string | undefined) =>
        fetchJson<PinList>(`/api/${scope}/${id}/pins`, token),
    setPin: (scope: PinScope, id: string, messageId: string, pin: boolean, token: string | undefined) =>
        fetchProblem<void>(`/api/${scope}/${id}/pins/${messageId}`, token, { method: pin ? "POST" : "DELETE" }),

    searchConversation: (scope: ConversationScopeKind, id: string, q: string, before: string | null, token: string | undefined) => {
        const qs = new URLSearchParams({ q, take: "30" });
        if (before) qs.set("before", before);
        return fetchProblem<ConversationSearchPage>(`/api/${scope}/${id}/search?${qs.toString()}`, token);
    },

    createPoll: (scope: ConversationScopeKind, id: string, payload: CreatePollPayload, token: string | undefined) =>
        fetchProblem<PollView>(`/api/${scope}/${id}/polls`, token, { method: "POST", body: JSON.stringify(payload) }),
    getPoll: (pollId: string, token: string | undefined) =>
        fetchProblem<PollView>(`/api/polls/${pollId}`, token),
    /** Set / change my vote; an empty list withdraws it. */
    votePoll: (pollId: string, optionIds: string[], token: string | undefined) =>
        fetchProblem<void>(`/api/polls/${pollId}/vote`, token, { method: "POST", body: JSON.stringify({ optionIds }) }),
    closePoll: (pollId: string, token: string | undefined) =>
        fetchProblem<void>(`/api/polls/${pollId}/close`, token, { method: "POST" }),
    /** Creator only — cleans up a poll whose message couldn't be posted. */
    deletePoll: (pollId: string, token: string | undefined) =>
        fetchProblem<void>(`/api/polls/${pollId}`, token, { method: "DELETE" })
};
